import { ChangeDetectionStrategy, Component, computed, ElementRef, inject, input, linkedSignal, output } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { TranslocoModule } from '@jsverse/transloco';
import { DatePickerModule } from 'primeng/datepicker';
import { DatepickerFastTypingDirective } from '../../../directives/datepicker-fast-typing.directive';
import { DatepickerSelectOnClickDirective } from '../../../directives/datepicker-select-on-click.directive';
import { isEndBeforeStart } from '../../../utils/date-comparison.utils';
import { CesButtonSecondaryComponent } from '../ces-buttons/ces-button-secondary.component';
import { CesButtonTertiaryComponent } from '../ces-buttons/ces-button-tertiary.component';
import { DATE_PICKER_CONFIG } from './ces-date-navigation.constants';

@Component({
  selector: 'ces-date-navigation',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    FormsModule,
    DatePickerModule,
    CesButtonTertiaryComponent,
    CesButtonSecondaryComponent,
    TranslocoModule,
    DatepickerSelectOnClickDirective,
    DatepickerFastTypingDirective,
  ],
  templateUrl: './ces-date-navigation.component.html',
})
export class CesDateNavigationComponent {
  readonly startDate = input.required<Date | null>();
  readonly endDate = input.required<Date | null>();
  readonly canNavigate = input.required<boolean>();
  readonly endDateDisabled = input.required<boolean>();
  readonly refreshDisabled = input<boolean>(false);

  readonly startDateChange = output<Date | null>();
  readonly endDateChange = output<Date | null>();
  readonly navigatePrevious = output<void>();
  readonly navigateNext = output<void>();
  readonly refresh = output<void>();

  readonly datePickerConfig = DATE_PICKER_CONFIG;

  private readonly host = inject<ElementRef<HTMLElement>>(ElementRef);

  // What the operator just typed, mirrored before the parent echoes it back through the
  // inputs, so the Enter gate judges the new range synchronously.
  private readonly stagedStart = linkedSignal(() => this.startDate());
  private readonly stagedEnd = linkedSignal(() => this.endDate());

  // Shows the constraint message and holds the refresh while the range is inverted.
  readonly dateRangeInvalid = computed(() => isEndBeforeStart(this.stagedStart(), this.stagedEnd()));

  onStartDateChange(date: Date | null): void {
    this.stagedStart.set(date);
    this.startDateChange.emit(date);
  }

  onEndDateChange(date: Date | null): void {
    this.stagedEnd.set(date);
    this.endDateChange.emit(date);
  }

  /** Enter on the end date refreshes, unless the range it completes is inverted. */
  onEndDateEnter(): void {
    if (!this.dateRangeInvalid()) this.refresh.emit();
  }

  /** Enter on the start date flows into the end date; only Enter there refreshes. */
  focusEndDate(): void {
    const input = this.host.nativeElement.querySelector<HTMLInputElement>('#end-date');
    if (!input || input.disabled) return;
    input.focus();
    input.select();
  }
}
